import type { ApplicationSetting, SettingEnumOption } from "../../models";
import type { DefinitionTree, Office, Segment, SettingGridRow } from "./settingsTree";

import { ApplicationSettingType } from "../../models";
import { changePick } from "./segmentPicks";
import { isPlaceholder } from "./settingsTree";

/**
 * A stored value in the form its input shows: "true"/"false" for a Boolean (stored as 1/0 or true/false),
 * and the number for an Enum (stored as the number, name or label). Anything else is left as it is.
 */
export function normalizeValue(
	value: string | null | undefined,
	type?: ApplicationSettingType | null,
	options: SettingEnumOption[] = [],
) {
	const trimmed = (value ?? "").trim();
	const lower = trimmed.toLowerCase();
	if (type === ApplicationSettingType.Boolean) {
		if (lower === "1" || lower === "true") return "true";
		if (lower === "0" || lower === "false") return "false";
	}
	if (type === ApplicationSettingType.Enum) {
		const option = findEnumOption(value, options);
		if (option) return option.value;
	}
	return value ?? "";
}

/** The option whose number, name or label is the value, ignoring case and padding, as the server matches them when saving */
function findEnumOption(value: string | null | undefined, options: SettingEnumOption[]) {
	const lower = (value ?? "").trim().toLowerCase();
	return options.find((o) => [o.value, o.name, o.label].some((text) => text.toLowerCase() === lower));
}

/**
 * How an Enum setting's stored value reads: its option's label, or the value itself, unmatched, when no option has
 * it as its number, name or label. Undefined when there's no value or the setting has no options.
 */
export function enumValue(value: string | null | undefined, options: SettingEnumOption[]) {
	if (value == null || !options.length) return undefined;
	const option = findEnumOption(value, options);
	return option ? { label: option.label, matched: true } : { label: value, matched: false };
}

/**
 * The name the definition cascade picks, split into parts; [Placeholder] parts still need filling in.
 * The setting goes under the deepest picked container, even when it's a suffix inherited from a level above.
 */
export function pickedNameParts(tree: DefinitionTree, path: string[], leaf?: string) {
	const node = leaf ? tree.nodes.get(leaf.toLowerCase()) : undefined;
	if (!node || node.definition.isContainer) return undefined;
	const parent = path.length ? tree.nodes.get(path[path.length - 1].toLowerCase()) : undefined;
	if (path.length && !parent) return undefined;
	const name = parent ? `${parent.definition.fullName}.${node.definition.name}` : node.definition.fullName;
	return name.split(".");
}

/** The name with each [Placeholder] part replaced by its fill, in order; undefined while a fill is missing or holds a dot */
export function fillPlaceholders(parts: string[], fills: string[]) {
	let next = 0;
	const filled = parts.map((part) => (isPlaceholder(part) ? fills[next++]?.trim() : part));
	return filled.every((part) => part && !part.includes(".")) ? filled.join(".") : undefined;
}

/**
 * For each [Placeholder] part, in order, the names its definition's enum allows in its place (such as Landline, Mobile
 * and Voip for [CarrierType]). Empty when the placeholder takes any text
 */
export function placeholderOptions(tree: DefinitionTree, parts: string[]) {
	return parts.flatMap((part, i) => {
		if (!isPlaceholder(part)) return [];
		const node = tree.nodes.get(
			parts
				.slice(0, i + 1)
				.join(".")
				.toLowerCase(),
		);
		return [node?.definition.enumOptions ?? []];
	});
}

/** The segment ids to save. An office row can't have other segments, so with an office only the office is kept */
export function segmentToSave(segment: Segment): Required<Segment> {
	if (segment.branchId != null)
		return { brandId: null, channelId: null, marketerId: null, usStateId: null, branchId: segment.branchId };
	return {
		brandId: segment.brandId ?? null,
		channelId: segment.channelId ?? null,
		marketerId: segment.marketerId ?? null,
		usStateId: segment.usStateId ?? null,
		branchId: null,
	};
}

/** The segment to start editing a row from. An office row also shows its office's brand, channel, marketer and state */
export function segmentOfRow(row: ApplicationSetting, offices: Office[] = []): Segment {
	if (row.branchId != null) return changePick({}, "branchId", row.branchId, offices);
	return { brandId: row.brandId, channelId: row.channelId, marketerId: row.marketerId, usStateId: row.usStateId };
}

/**
 * What a grid row offers: Edit and Delete when its value comes from one of its own rows,
 * or Add override when it's a default, inherited, or not set. Orphans aren't read, so they don't get Add override.
 */
export function rowAction(row: SettingGridRow): "edit" | "addOverride" | undefined {
	if (row.winner && row.rows.includes(row.winner)) return "edit";
	if (!row.isOrphaned && (row.source === "default" || row.source === "inherited" || row.source === "none"))
		return "addOverride";
	return undefined;
}
